import { useEffect, useRef } from 'react';
import type { SfxName } from '../../../audio/audioEngine';
import { audioEngine } from '../../../audio/audioEngine';
import { readDomMood } from './readDomMood';
import { getActiveMinigameSeUrl } from './minigameSe';

/**
 * Foundation helper: useMountSound（R2 Wave2B・2026-06-21）
 *
 * 「このコンポーネントが表示された瞬間に SFX を 1 回鳴らす」共通フック。
 * JudgementModal（success）/ FailOverlay（fail）が使う。
 *
 * 設計上の肝:
 *  - mood は **発火の瞬間** に readDomMood() で解決する（semantic は mood 引数を持たない）。
 *    → 画面の data-mood と音色が自動一致。
 *  - audioEngine を直接叩く（AudioProvider 非依存・Sandbox でも鳴る）。unlock 前/mute 中は
 *    audioEngine 側が安全に無視するので、ここで条件判定は不要。
 *  - **React StrictMode の二重 mount** で二重に鳴らないよう ref ガードを張る。
 *    StrictMode は dev で effect を mount→unmount→mount と 2 回流すため、素朴な
 *    空依存 useEffect だと 2 回発音してしまう。playedRef で「1 度鳴らしたら以後鳴らさない」。
 *  - `enabled` が false の間は鳴らさない（例: JudgementModal は type が success の時だけ鳴らす）。
 *    enabled が false→true に変わった最初のレンダで 1 回だけ鳴る。
 *
 * 注意: このフックは「マウント存在中に一度」の意味で、同じインスタンスが
 * 非表示↔表示を繰り返しても再発音しない（semantic は出す/出さないを
 * 描画有無で制御する前提のため、これで十分）。再演出が要るケースは現状なし。
 */
export function useMountSound(name: SfxName, enabled = true): void {
  const playedRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    if (playedRef.current) return;
    playedRef.current = true;
    // P8-1: config.audio.successSE/failSE（アセット SE）が指定されていればそれを優先する。
    // 未指定・未登録（Sandbox 等）は従来どおり合成 SFX（挙動不変）。
    const assetUrl =
      name === 'success' || name === 'fail' ? getActiveMinigameSeUrl(name) : undefined;
    if (assetUrl) {
      audioEngine.playSfxAsset(assetUrl);
    } else {
      const mood = readDomMood();
      audioEngine.playSfx(name, mood ? { mood } : undefined);
    }
    // enabled を依存に含め、false→true の遷移で 1 回だけ発火できるようにする。
    // name は実質固定（呼び出し側はリテラル）だが、lint 充足のため依存に入れる。
  }, [enabled, name]);
}
